import React, { useState, useEffect, useRef } from 'react';
import { useSilentSaveListener } from '../../../hooks/useSilentSaveListener';
import { useSafeSave } from '../../../hooks/useSafeSave';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import BusinessValuesHeader from './BusinessValuesHeader';
import BusinessValuesAdd from './BusinessValuesAdd';
import BusinessValueListView from './BusinessValueListView';
import BusinessValueChartView from './BusinessValueChartView';
import GuideModal from './GuideModal';
import { getShortId, ensureShortIds, ensureSupportAssetShortIds, getSupportAssetShortId } from './businessValuesUtils';
import { useBroadcast } from '../../../hooks/useBroadcast';
import useAnalysisStore from '../../../stores/useAnalysisStore';
import useAtelier1Store from '../../../stores/useAtelier1Store';
import { showSuccessToast, showErrorToast, showLoadingToast, updateToast, showInfoToast } from '../../../utils/toastUtils';
import { createIdGenerator } from '../../../utils/idGenerator'; // Import ID generator
import { PlusCircle, Info, Trash2, Edit2, ChevronDown, ChevronRight, Search, Plus, FileText, AlertTriangle, X, Check, Save } from 'lucide-react';
import { useMemo } from 'react';

// Asset Types will be translated dynamically

// Ultra Modern Compact Inline Add Form Component
const InlineAddForm = ({
  isVisible,
  newBusinessValue,
  setNewBusinessValue,
  newBusinessValueDescription,
  setNewBusinessValueDescription,
  newNature,
  setNewNature,
  newProprietaireNom,
  setNewProprietaireNom,
  newProprietaireType,
  setNewProprietaireType,
  handleAddBusinessValue,
  onCancel
}) => {
  const { t } = useTranslation();

  const handleAddClick = () => {
    if (newBusinessValue.trim()) {
      handleAddBusinessValue();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddClick();
    }
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, height: 0, y: -20 }}
      animate={{ opacity: 1, height: 'auto', y: 0 }}
      exit={{ opacity: 0, height: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="mb-6 overflow-hidden"
    >
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
              <PlusCircle size={20} className="text-blue-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-800">
                {t('businessValues.modal.addTitle')}
              </h3>
              <p className="text-xs text-gray-500">Ajouter une nouvelle valeur métier</p>
            </div>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Compact Form Fields */}
        <div className="space-y-3 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('businessValues.fields.businessValueName')} <span className="text-red-500">*</span>
            </label>
            <input
              id="businessValueName"
              type="text"
              value={newBusinessValue}
              onChange={(e) => setNewBusinessValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={t('businessValues.modal.namePlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-shadow duration-200"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('businessValues.fields.businessValueDescription')}
            </label>
            <textarea
              id="businessValueDescription"
              value={newBusinessValueDescription}
              onChange={(e) => setNewBusinessValueDescription(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={t('businessValues.modal.descriptionPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-shadow duration-200"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('businessValues.fields.nature')} <span className="text-red-500">*</span>
              </label>
              <select
                value={newNature}
                onChange={(e) => setNewNature(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
              >
                <option value="processus">{t('businessValues.fields.natureProcessus')}</option>
                <option value="information">{t('businessValues.fields.natureInformation')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('businessValues.fields.proprietaireNom')}
              </label>
              <input
                type="text"
                value={newProprietaireNom}
                onChange={(e) => setNewProprietaireNom(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={t('businessValues.fields.proprietaireNom')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('businessValues.fields.proprietaireType')}
              </label>
              <select
                value={newProprietaireType}
                onChange={(e) => setNewProprietaireType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
              >
                <option value="interne">{t('businessValues.fields.proprietaireInterne')}</option>
                <option value="externe">{t('businessValues.fields.proprietaireExterne')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            {t('common.cancel')}
          </button>

          <button
            onClick={handleAddClick}
            disabled={!newBusinessValue.trim()}
            className={`px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium shadow-sm hover:bg-blue-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center ${!newBusinessValue.trim() ? 'opacity-50 cursor-not-allowed' : ''
              }`}
          >
            <PlusCircle size={16} className="mr-2" />
            {t('businessValues.buttons.add')}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const BusinessValues = () => {
  const { t } = useTranslation();

  // Define Asset Types with translations
  const ASSET_TYPES = [
    { value: 'organisation', label: t('businessValues.assetTypes.organisation') },
    { value: 'locaux', label: t('businessValues.assetTypes.locaux') },
    { value: 'equipements', label: t('businessValues.assetTypes.equipements') },
    { value: 'reseaux', label: t('businessValues.assetTypes.reseaux') },
    { value: 'logiciels', label: t('businessValues.assetTypes.logiciels') }
  ];

  const atelier1Store = useAtelier1Store();
  const analysisStore = useAnalysisStore();
  const broadcastUpdate = useBroadcast();
  const isWorkshopDataLoading = analysisStore.isWorkshopDataLoading;
  const workshopDataError = analysisStore.workshopDataError;

  const currentBusinessValues = atelier1Store.businessValues;
  const currentOrphanedSupportAssets = atelier1Store.orphanedSupportAssets;
  const currentAnalysis = analysisStore.currentAnalysis;

  // Initialize ID generator for this analysis
  const idGen = currentAnalysis?.id ? createIdGenerator({ analysisId: currentAnalysis.id }) : null;

  // Safe save hook - prevents auto-save from overwriting data when not fully loaded
  const { markDataLoaded, markModified, canAutoSave, clearModified, useFormChangeDetector } = useSafeSave('BusinessValues');
  const formChangeRef = useFormChangeDetector();

  // Local state for UI and form handling
  const [businessValues, setBusinessValues] = useState([]);
  const [orphanedSupportAssets, setOrphanedSupportAssets] = useState([]);
  const [newBusinessValue, setNewBusinessValue] = useState("");
  const [newBusinessValueDescription, setNewBusinessValueDescription] = useState('');
  const [newNature, setNewNature] = useState('processus');
  const [newProprietaireNom, setNewProprietaireNom] = useState('');
  const [newProprietaireType, setNewProprietaireType] = useState('interne');
  const [editingValue, setEditingValue] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editNature, setEditNature] = useState('processus');
  const [editProprietaireNom, setEditProprietaireNom] = useState('');
  const [editProprietaireType, setEditProprietaireType] = useState('interne');
  const [expandedValue, setExpandedValue] = useState(null);
  const [newSupportAssetName, setNewSupportAssetName] = useState('');
  const [newSupportAssetDescription, setNewSupportAssetDescription] = useState('');
  const [newSupportAssetType, setNewSupportAssetType] = useState(ASSET_TYPES[0].value);
  const [newSupportAssetOwner, setNewSupportAssetOwner] = useState('');
  const [newSupportAssetLocation, setNewSupportAssetLocation] = useState('');
  
  // Refs to keep track of the latest state to avoid stale closure issues during auto-saves
  const businessValuesRefLatest = useRef(businessValues);
  const orphanedSupportAssetsRefLatest = useRef(orphanedSupportAssets);

  // Enhanced fields for CPE discovery
  const [newSupportAssetVendor, setNewSupportAssetVendor] = useState('');
  const [newSupportAssetProduct, setNewSupportAssetProduct] = useState('');
  const [newSupportAssetVersion, setNewSupportAssetVersion] = useState('');
  const [discoveredCPE, setDiscoveredCPE] = useState('');
  const [cpeConfidence, setCpeConfidence] = useState('');
  const [cpeSearching, setCpeSearching] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [selectedValueForChart, setSelectedValueForChart] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(0.75); // Start with 1.5x zoom out (0.75 zoom level)
  const [viewportPosition, setViewportPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [useShortIds, setUseShortIds] = useState(true);
  const chartContainerRef = useRef(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // New state for inline form and help section visibility
  const [isAddFormVisible, setIsAddFormVisible] = useState(false);
  const [isHelpVisible, setIsHelpVisible] = useState(true); // Start with help visible
  const [isGuideOpen, setIsGuideOpen] = useState(false); // State for guide modal

  // --- State for Editing Support Assets ---
  const [editingAsset, setEditingAsset] = useState(null); // Holds the asset object being edited {businessValueId: string, asset: object}

  // Derived state to get all available existing support assets across all business values
  const allExistingSupportAssets = useMemo(() => {
    const assetsMap = new Map();
    businessValues.forEach(bv => {
      (bv.supportAssets || []).forEach(asset => {
        // Prevent dupes by using asset.id (or a combination like name+type if id was previously local-only)
        if (!assetsMap.has(asset.id)) {
          assetsMap.set(asset.id, asset);
        }
      });
    });
    // Add orphaned assets to the map
    orphanedSupportAssets.forEach(asset => {
      if (!assetsMap.has(asset.id)) {
        assetsMap.set(asset.id, asset);
      }
    });

    return Array.from(assetsMap.values());
  }, [businessValues, orphanedSupportAssets]);

  // CPE Discovery Service
  const discoverCPE = async (vendor, product, version) => {
    if (!vendor || !product) return null;

    setCpeSearching(true);
    try {
      // Method 1: Try CIRCL CVE Search API (free)
      const searchQuery = `${vendor} ${product} ${version} `.trim();
      const response = await fetch(`https://cve.circl.lu/api/search/${encodeURIComponent(searchQuery)}`);

      if (response.ok) {
        const data = await response.json();
        if (data && data.length > 0) {
          // Look for CPE in the first result
          const firstResult = data[0];
          if (firstResult.vulnerable_configuration && firstResult.vulnerable_configuration.length > 0) {
            const cpe = firstResult.vulnerable_configuration[0];
            setDiscoveredCPE(cpe);
            setCpeConfidence('High');
            return cpe;
          }
        }
      }

      // Method 2: Generate CPE based on common patterns (fallback)
      const generatedCPE = generateCPE(vendor, product, version);
      setDiscoveredCPE(generatedCPE);
      setCpeConfidence('Generated');
      return generatedCPE;

    } catch (error) {
      console.error('CPE discovery error:', error);
      // Fallback to generated CPE
      const generatedCPE = generateCPE(vendor, product, version);
      setDiscoveredCPE(generatedCPE);
      setCpeConfidence('Generated');
      return generatedCPE;
    } finally {
      setCpeSearching(false);
    }
  };

  // Generate CPE based on common patterns
  const generateCPE = (vendor, product, version) => {
    const cleanVendor = vendor.toLowerCase().replace(/[^a-z0-9]/g, '_');
    const cleanProduct = product.toLowerCase().replace(/[^a-z0-9]/g, '_');
    const cleanVersion = version || '*';

    return `cpe:2.3:a:${cleanVendor}:${cleanProduct}:${cleanVersion}:*:*:*:*:*:*:*`;
  };

  // Auto-discover CPE when vendor/product/version change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (newSupportAssetVendor && newSupportAssetProduct) {
        discoverCPE(newSupportAssetVendor, newSupportAssetProduct, newSupportAssetVersion);
      } else {
        setDiscoveredCPE('');
        setCpeConfidence('');
      }
    }, 1000); // Debounce for 1 second

    return () => clearTimeout(timeoutId);
  }, [newSupportAssetVendor, newSupportAssetProduct, newSupportAssetVersion]);

  // Update local state when context data changes
  useEffect(() => {
    if (currentBusinessValues && Array.isArray(currentBusinessValues)) {
      // Filter out system VMs (hidden global VM for unlinked ERs)
      const filtered = currentBusinessValues.filter(v => !v.isSystem);
      // Ensure all business values have shortIds
      let updatedValues = ensureShortIds(filtered);
      // Ensure all support assets have shortIds
      updatedValues = ensureSupportAssetShortIds(updatedValues);
      setBusinessValues(updatedValues);
      businessValuesRefLatest.current = updatedValues;

      const oAssets = currentOrphanedSupportAssets || [];
      setOrphanedSupportAssets(oAssets);
      orphanedSupportAssetsRefLatest.current = oAssets;

      // ✅ Mark data as loaded - auto-save is now safe
      markDataLoaded();
    }
  }, [currentBusinessValues, currentOrphanedSupportAssets]);

  // Initialize chart view with first business value if none selected
  useEffect(() => {
    if (viewMode === 'chart') {
      // Reset zoom to 0.75 (1.5x zoom out) when switching to chart view
      setZoomLevel(0.75);
      setViewportPosition({ x: 0, y: 0 });

      // Select first business value if none selected
      if (businessValues.length > 0 && !selectedValueForChart) {
        setSelectedValueForChart(businessValues[0].id);
      }
    }
  }, [viewMode, businessValues, selectedValueForChart]);

  // Make export function globally available for the header button
  useEffect(() => {
    window.exportBusinessValuesToPDF = exportBusinessValuesToPDF;
    return () => {
      delete window.exportBusinessValuesToPDF;
    };
  }, []);

  // Function to save business values to the backend
  const handleSaveData = async () => {
    console.log("Saving business values:", businessValues);
    // Show loading toast
    const toastId = showLoadingToast(t('businessValues.status.saving'));

    const currentBValues = businessValuesRefLatest.current;
    const currentOAssets = orphanedSupportAssetsRefLatest.current;

    try {
      const response = await useAtelier1Store.getState().saveBusinessValues(currentBValues, currentOAssets, broadcastUpdate);
      if (response.success) {
        // Update loading toast to success
        updateToast(toastId, t('businessValues.status.saved'), 'success');

        // Reset unsaved changes flag
        setHasUnsavedChanges(false);
      } else {
        // Update loading toast to error
        updateToast(toastId, `${t('businessValues.status.saveError')}: ${response.message || 'Erreur inconnue'}`, 'error');
      }
    } catch (error) {
      console.error('Error saving business values:', error);
      // Update loading toast to error
      updateToast(toastId, `${t('businessValues.status.saveError')}: ${error.message || 'Erreur inconnue'}`, 'error');
    }
  };

  // Silent save function for auto-save (no toast notifications)
  // GUARDED: Will only save if data was loaded AND user made modifications
  const handleSilentSave = async () => {
    // ⛔ Safety guard: don't save if data hasn't loaded or user hasn't changed anything
    if (!canAutoSave()) {
      return 'skipped'; // Signal to parent that no save was needed
    }

    const currentBValues = businessValuesRefLatest.current;
    const currentOAssets = orphanedSupportAssetsRefLatest.current;

    try {
      const response = await useAtelier1Store.getState().saveBusinessValues(currentBValues, currentOAssets, broadcastUpdate);
      if (response.success) {
        setHasUnsavedChanges(false);
        clearModified();
      }
      return response.success;
    } catch (error) {
      console.error('Silent save error business values:', error);
      return false;
    }
  };

  useSilentSaveListener('business-values', handleSilentSave);


  // Functions to manage business values
  const handleAddBusinessValue = () => {
    if (newBusinessValue.trim()) {
      // Generate new ID using ID generator
      const newId = idGen ? idGen.generateNext('businessValue', businessValues) : `temp-vm-${Date.now()}`;
      const newValue = {
        id: newId,
        name: newBusinessValue,
        description: newBusinessValueDescription,
        nature: newNature,
        proprietaire: {
          nom: newProprietaireNom,
          type: newProprietaireType
        },
        shortId: getShortId(businessValues.length),
        supportAssets: [],
        securityPillars: []
      };

      setBusinessValues(prev => {
        const next = [...prev, newValue];
        businessValuesRefLatest.current = next;
        return next;
      });
      setNewBusinessValue('');
      setNewBusinessValueDescription('');
      setNewNature('processus');
      setNewProprietaireNom('');
      setNewProprietaireType('interne');
      setIsAddFormVisible(false); // Close form on add

      // Set unsaved changes flag
      setHasUnsavedChanges(true);
      markModified();

      // Show success toast
      showSuccessToast(t('businessValues.status.valueAdded', { name: newValue.name }));
    } else {
      // Show error toast if name is empty
      showErrorToast(t('businessValues.status.emptyNameError'));
    }
  };

  const handleCancelAddForm = () => {
    setNewBusinessValue('');
    setNewBusinessValueDescription('');
      setNewNature('processus');
      setNewProprietaireNom('');
      setNewProprietaireType('interne');
    setIsAddFormVisible(false);
  };

  const handleRemoveBusinessValue = (id) => {
    // Find the business value to be removed for the toast message
    const valueToRemove = businessValues.find(value => value.id === id);

    // Update state
    setBusinessValues(prev => {
      const next = prev.filter(value => value.id !== id);
      businessValuesRefLatest.current = next;
      return next;
    });
    if (selectedValueForChart === id) {
      const remaining = businessValuesRefLatest.current;
      setSelectedValueForChart(remaining[0]?.id || null);
    }
    if (expandedValue === id) setExpandedValue(null);

    // Set unsaved changes flag
    setHasUnsavedChanges(true);
    markModified();

    // Show toast notification
    if (valueToRemove) {
      showSuccessToast(t('businessValues.status.valueRemoved', { name: valueToRemove.name }));
    }
  };

  // --- Support asset management ---
  const handleAddSupportAsset = (businessValueId) => {
    if (!newSupportAssetName.trim()) return; // Check name specifically

    // Find the parent business value to get its ID for child ID generation
    const parentBusinessValue = businessValues.find(bv => bv.id === businessValueId);

    // Generate new ID using ID generator (child ID with parent reference)
    const newAssetId = idGen && parentBusinessValue?.id
      ? idGen.generateNextChild(parentBusinessValue.id, 'supportAsset', parentBusinessValue.supportAssets || [])
      : `temp-bs-${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const newAsset = {
      id: newAssetId,
      name: newSupportAssetName,
      description: newSupportAssetDescription,
      type: newSupportAssetType,
      owner: newSupportAssetOwner,
      location: newSupportAssetLocation,
      // Enhanced CPE fields
      vendor: newSupportAssetVendor,
      product: newSupportAssetProduct,
      version: newSupportAssetVersion,
      cpe: discoveredCPE,
      cpeConfidence: cpeConfidence,
      // shortId will be added below dynamically
    };

    setBusinessValues(currentBusinessValues => {
      const updated = currentBusinessValues.map(value => {
        if (value.id !== businessValueId) return value;

        const updatedAssets = [...(value.supportAssets || [])];
        // Add shortId based on the *new* length
        newAsset.shortId = getSupportAssetShortId(value.shortId, updatedAssets.length);
        updatedAssets.push(newAsset);

        return { ...value, supportAssets: updatedAssets };
      });
      businessValuesRefLatest.current = updated;
      return updated;
    });

    // Set unsaved changes flag
    setHasUnsavedChanges(true);
    markModified();

    // Reset add form fields
    setNewSupportAssetName('');
    setNewSupportAssetDescription('');
    setNewSupportAssetType(ASSET_TYPES[0].value);
    setNewSupportAssetOwner('');
    setNewSupportAssetLocation('');
    // Reset CPE fields
    setNewSupportAssetVendor('');
    setNewSupportAssetProduct('');
    setNewSupportAssetVersion('');
    setDiscoveredCPE('');
    setCpeConfidence('');
  };

  const handleLinkExistingSupportAsset = (businessValueId, existingAsset) => {
    setBusinessValues(currentBusinessValues => {
      const updated = currentBusinessValues.map(value => {
        if (value.id !== businessValueId) return value;

        const updatedAssets = [...(value.supportAssets || [])];

        // Prevent linking the exact same asset twice in the same VM
        if (updatedAssets.some(a => a.id === existingAsset.id)) {
          return value;
        }

        // Add shortId based on the *new* length, copying the rest of existingAsset properties
        const newAsset = { ...existingAsset };
        newAsset.shortId = getSupportAssetShortId(value.shortId, updatedAssets.length);

        updatedAssets.push(newAsset);

        return { ...value, supportAssets: updatedAssets };
      });
      businessValuesRefLatest.current = updated;
      return updated;
    });

    // Remove from orphaned list if it was there
    setOrphanedSupportAssets(prev => {
      const next = prev.filter(a => a.id !== existingAsset.id);
      orphanedSupportAssetsRefLatest.current = next;
      return next;
    });

    // Set unsaved changes flag
    setHasUnsavedChanges(true);
    markModified();

    // Close modal implicitly handled by parent UI
  };

  const handleRemoveSupportAsset = (businessValueId, assetId) => {
    let removedAsset = null;
    const value = businessValues.find(v => v.id === businessValueId);
    if (value) {
      removedAsset = (value.supportAssets || []).find(a => a.id === assetId);
    }

    setBusinessValues(prev => {
      const next = prev.map(value =>
        value.id !== businessValueId ? value : {
          ...value,
          supportAssets: (value.supportAssets || []).filter(asset => asset.id !== assetId)
        }
      );
      businessValuesRefLatest.current = next;
      return next;
    });

    // Determine if it was the last instance of this asset across all VMs
    if (removedAsset) {
      const isLinkedElsewhere = businessValuesRefLatest.current.some(v => 
        v.id !== businessValueId && (v.supportAssets || []).some(a => a.id === assetId)
      );

      if (!isLinkedElsewhere) {
        // If it's not linked anywhere else, add it to orphaned list
        setOrphanedSupportAssets(prev => {
          if (!prev.some(a => a.id === assetId)) {
            const next = [...prev, removedAsset];
            orphanedSupportAssetsRefLatest.current = next;
            return next;
          }
          return prev;
        });
      }
    }

    // If the removed asset was being edited, cancel edit mode
    if (editingAsset && editingAsset.asset.id === assetId) {
      setEditingAsset(null);
    }
    
    setHasUnsavedChanges(true); // Ensure save triggers on unlink
    markModified();
  };

  const handleGlobalDeleteSupportAsset = (assetId) => {
    setBusinessValues(currentBusinessValues => {
      const next = currentBusinessValues.map(value => ({
        ...value,
        supportAssets: (value.supportAssets || []).filter(asset => asset.id !== assetId)
      }));
      businessValuesRefLatest.current = next;
      return next;
    });
    setOrphanedSupportAssets(prev => {
      const next = prev.filter(asset => asset.id !== assetId);
      orphanedSupportAssetsRefLatest.current = next;
      return next;
    });
    if (editingAsset && editingAsset.asset.id === assetId) {
      setEditingAsset(null);
    }
    setHasUnsavedChanges(true);
    markModified();
    showSuccessToast(t('businessValues.status.assetDeleted', 'Bien support supprimé globalement'));
  };

  // --- Handlers for Editing Support Assets ---
  const handleStartEditSupportAsset = (businessValueId, asset) => {
    setEditingAsset({ businessValueId, asset: { ...asset } }); // Store BV ID and a copy of the asset
  };

  const handleCancelEditSupportAsset = () => {
    setEditingAsset(null);
  };

  const handleEditSupportAssetChange = (field, value) => {
    if (!editingAsset) return;
    setEditingAsset(prev => ({
      ...prev,
      asset: { ...prev.asset, [field]: value }
    }));
  };

  const handleEditSupportAssetSave = () => {
    if (!editingAsset) return;
    const { businessValueId, asset: editedAsset } = editingAsset;

    // Basic validation
    if (!editedAsset.name || !editedAsset.name.trim()) {
      showErrorToast(t('businessValues.status.emptyNameError'));
      return;
    }

    // Update the asset across ALL business values where it's linked
    setBusinessValues(currentBusinessValues => {
      const updated = currentBusinessValues.map(value => {
        // Only map over supportAssets if the VM has any
        if (!value.supportAssets || value.supportAssets.length === 0) return value;

        // Check if this VM has the asset we are currently editing
        const hasAsset = value.supportAssets.some(a => a.id === editedAsset.id);
        if (!hasAsset) return value;

        return {
          ...value,
          supportAssets: value.supportAssets.map(asset =>
            asset.id === editedAsset.id ? { ...editedAsset, shortId: asset.shortId } : asset
          )
        };
      });
      businessValuesRefLatest.current = updated;
      return updated;
    });
    
    // Also update in orphaned assets if present
    setOrphanedSupportAssets(prev => {
      if (prev.some(a => a.id === editedAsset.id)) {
        const next = prev.map(a => a.id === editedAsset.id ? editedAsset : a);
        orphanedSupportAssetsRefLatest.current = next;
        return next;
      }
      return prev;
    });

    setEditingAsset(null); // Exit edit mode
  };

  // Security pillar management
  const handleToggleSecurityPillar = (businessValueId, pillarId) => {
    setBusinessValues(prev => {
      const next = prev.map(value => {
        if (value.id !== businessValueId) return value;
        const securityPillars = value.securityPillars || [];
        const isPillarSelected = securityPillars.includes(pillarId);
        return {
          ...value,
          securityPillars: isPillarSelected
            ? securityPillars.filter(id => id !== pillarId)
            : [...securityPillars, pillarId]
        };
      });
      businessValuesRefLatest.current = next;
      return next;
    });
  };

  // Editing business values
  const handleStartEdit = (value) => {
    setEditingValue(value.id);
    setEditValue(value.name);
    setEditDescription(value.description || '');
    setEditNature(value.nature || 'processus');
    setEditProprietaireNom(value.proprietaire?.nom || '');
    setEditProprietaireType(value.proprietaire?.type || 'interne');
  };

  const handleEditSave = (id) => {
    if (editValue.trim()) {
      setBusinessValues(prev => {
        const next = prev.map(value =>
          value.id === id ? { ...value, name: editValue, description: editDescription, nature: editNature, proprietaire: { nom: editProprietaireNom, type: editProprietaireType } } : value
        );
        businessValuesRefLatest.current = next;
        return next;
      });
      setEditingValue(null);
      setEditValue('');
      setEditDescription('');
    }
  };

  const handleCancelEdit = () => {
    setEditingValue(null);
    setEditValue('');
    setEditDescription('');
    setEditNature('processus');
    setEditProprietaireNom('');
    setEditProprietaireType('interne');
  };





  // PDF Export Handler (adapted from existing exportBusinessValuesToPDF)
  const handleExportPDF = async () => {
    try {
      console.log('[PDF Export] Starting Business Values PDF generation...');
      console.log('[PDF Export] Business values state:', businessValues);
      console.log('[PDF Export] Current business values from context:', currentBusinessValues);

      // Extract business values - handle both direct array and nested structure
      let dataToExport = businessValues;

      // If businessValues is empty but currentBusinessValues exists, try to extract from it
      if ((!dataToExport || dataToExport.length === 0) && currentBusinessValues) {
        // Check if it's nested in data.businessValues
        if (currentBusinessValues.data && Array.isArray(currentBusinessValues.data.businessValues)) {
          dataToExport = currentBusinessValues.data.businessValues;
          console.log('[PDF Export] Extracted from nested structure:', dataToExport);
        } else if (Array.isArray(currentBusinessValues)) {
          dataToExport = currentBusinessValues;
          console.log('[PDF Export] Using direct array:', dataToExport);
        }
      }

      console.log('[PDF Export] Final data to export:', dataToExport);
      console.log('[PDF Export] Number of business values:', dataToExport?.length || 0);

      if (!dataToExport || dataToExport.length === 0) {
        showErrorToast('Aucune valeur métier à exporter. Veuillez d\'abord créer des valeurs métier.');
        return;
      }

      // Import PDF generator dynamically
      const { default: BusinessValuesPdfGenerator } = await import('../../../utils/businessValuesPdfGenerator.js');

      // Create PDF generator instance with business values data
      const pdfGenerator = new BusinessValuesPdfGenerator(dataToExport, {
        name: 'Business Values Analysis',
        companyName: 'Organisation',
        createdBy: 'Système EBIOS RM'
      });

      // Generate PDF
      const pdfBlob = await pdfGenerator.generateBusinessValuesReport();

      // Download PDF
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `EBIOS-RM_Valeurs-Metier_${'Organisation'}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);

      showSuccessToast('Rapport PDF généré avec succès');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showErrorToast(`Erreur lors de l'export PDF: ${error.message}`);
    }
  };

  // PDF Export Handler English
  const handleExportPDFEn = async () => {
    try {
      console.log('[PDF Export EN] Starting Business Values PDF generation...');

      // Extract business values - handle both direct array and nested structure
      let dataToExport = businessValues;

      // If businessValues is empty but currentBusinessValues exists, try to extract from it
      if ((!dataToExport || dataToExport.length === 0) && currentBusinessValues) {
        if (currentBusinessValues.data && Array.isArray(currentBusinessValues.data.businessValues)) {
          dataToExport = currentBusinessValues.data.businessValues;
        } else if (Array.isArray(currentBusinessValues)) {
          dataToExport = currentBusinessValues;
        }
      }

      if (!dataToExport || dataToExport.length === 0) {
        showErrorToast('No business values to export. Please create business values first.');
        return;
      }

      // Import bilingual PDF generator (English variant)
      const { default: BusinessValuesPdfGenerator } = await import('../../../utils/businessValuesPdfGenerator.js');

      // Create PDF generator instance with business values data
      const pdfGenerator = new BusinessValuesPdfGenerator(dataToExport, {
        name: 'Business Values Analysis',
        companyName: 'Organization',
        createdBy: 'EBIOS RM System'
      }, 'en');

      // Generate PDF
      const pdfBlob = await pdfGenerator.generateBusinessValuesReport();

      // Download PDF
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `EBIOS-RM_Business-Values_${'Organization'}_${new Date().toISOString().slice(0, 10)}_EN.pdf`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);

      showSuccessToast('PDF Report (EN) generated successfully');
    } catch (error) {
      console.error('Error exporting PDF (EN):', error);
      showErrorToast(`Error exporting PDF: ${error.message}`);
    }
  };

  // Legacy function for backward compatibility (used by window.exportBusinessValuesToPDF)
  const exportBusinessValuesToPDF = handleExportPDF;

  // DOCX Export Handler
  const handleExportDocx = async () => {
    try {
      let dataToExport = businessValues;

      if ((!dataToExport || dataToExport.length === 0) && currentBusinessValues) {
        if (currentBusinessValues.data && Array.isArray(currentBusinessValues.data.businessValues)) {
          dataToExport = currentBusinessValues.data.businessValues;
        } else if (Array.isArray(currentBusinessValues)) {
          dataToExport = currentBusinessValues;
        }
      }

      if (!dataToExport || dataToExport.length === 0) {
        showErrorToast('Aucune valeur métier à exporter. Veuillez d\'abord créer des valeurs métier.');
        return;
      }

      const { generateBusinessValuesDocx } = await import('../../../utils/businessValuesDocxGenerator.js');
      await generateBusinessValuesDocx(dataToExport, {
        name: 'Business Values Analysis',
        companyName: 'Organisation',
        createdBy: 'Système EBIOS RM'
      });

      showSuccessToast('Document Word généré avec succès');
    } catch (error) {
      console.error('Error exporting DOCX:', error);
      showErrorToast(`Erreur lors de l'export Word: ${error.message}`);
    }
  };

  // DOCX Export Handler (English)
  const handleExportDocxEn = async () => {
    try {
      let dataToExport = businessValues;

      if ((!dataToExport || dataToExport.length === 0) && currentBusinessValues) {
        if (currentBusinessValues.data && Array.isArray(currentBusinessValues.data.businessValues)) {
          dataToExport = currentBusinessValues.data.businessValues;
        } else if (Array.isArray(currentBusinessValues)) {
          dataToExport = currentBusinessValues;
        }
      }

      if (!dataToExport || dataToExport.length === 0) {
        showErrorToast('No business value to export. Please create business values first.');
        return;
      }

      const { generateBusinessValuesDocx } = await import('../../../utils/businessValuesDocxGenerator.js');
      await generateBusinessValuesDocx(dataToExport, {
        name: 'Business Values Analysis',
        companyName: 'Organization',
        createdBy: 'EBIOS RM System'
      }, 'en');

      showSuccessToast('Word document generated successfully');
    } catch (error) {
      console.error('Error exporting DOCX:', error);
      showErrorToast(`Error during Word export: ${error.message}`);
    }
  };

  // Import CSV Handler
  const handleImport = (file) => {
    if (!file) return;

    const allowedExtensions = ['.csv'];
    const allowedMimeTypes = ['text/csv', 'application/vnd.ms-excel', 'application/csv'];
    const fileName = file.name || '';
    const fileExt = fileName.split('.').pop()?.toLowerCase();
    const fileMime = file.type || '';

    if (!fileExt || !allowedExtensions.includes('.' + fileExt)) {
      showErrorToast('Seuls les fichiers CSV sont acceptés');
      return;
    }

    if (fileMime && !allowedMimeTypes.includes(fileMime)) {
      showErrorToast('Type de fichier non valide');
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const text = e.target.result;
        // Parse CSV - simple implementation handling semicolon or comma separator
        const lines = text.split(/\r\n|\n/);

        // Check for header to determine separator
        const firstLine = lines[0];
        const separator = firstLine.includes(';') ? ';' : ',';

        // Skip header
        const dataLines = lines.slice(1).filter(line => line.trim() !== '');

        // Temporary storage for grouped business values
        const groupedValues = {};

        dataLines.forEach(line => {
          // Handle potential quotes in CSV (basic implementation)
          let cols;
          if (line.includes('"')) {
            // Complex splitting for quoted values - simplified for this use case
            // For robust CSV parsing, a library like papaparse is recommended, but we keep it dependency-free here for now
            const regex = new RegExp(`(?:[^${separator}"]|"[^"]*")+`, 'g');
            cols = (line.match(regex) || []).map(val => val.replace(/^"|"$/g, '').trim());
          } else {
            cols = line.split(separator).map(val => val.trim());
          }

          if (cols.length < 1) return;

          const vmName = cols[0];
          const vmDesc = cols[1] || '';

          // Security Pillars (Any non-empty value indicates selection)
          // Cols 2-7: Confidentialité, Intégrité, Disponibilité, Traçabilité, Preuve, Auditabilité
          const pC = cols[2]?.trim();
          const pI = cols[3]?.trim();
          const pD = cols[4]?.trim();
          const pT = cols[5]?.trim();
          const pP = cols[6]?.trim();
          const pA = cols[7]?.trim();

          const bsName = cols[8] || '';
          const bsDesc = cols[9] || '';
          const bsType = cols[10] || 'equipements'; // Default type
          const bsOwner = cols[11] || '';
          const bsLocation = cols[12] || '';
          const bsVendor = cols[13] || '';
          const rawNature = (cols[16] || '').trim().toLowerCase();
          const vmNature = rawNature.startsWith('process') ? 'processus' : rawNature === 'information' ? 'information' : 'processus';
          const vmProprietaireNom = cols[17] || '';
          const rawPropType = (cols[18] || '').trim().toLowerCase();
          const vmProprietaireType = rawPropType.startsWith('extern') ? 'externe' : 'interne';
          const bsProduct = cols[14] || '';
          const bsVersion = cols[15] || '';

          if (!vmName) return;

          if (!groupedValues[vmName]) {
            // Determine active security pillars based on CSV values (if not empty)
            const activePillars = [];
            if (pC) activePillars.push('confidentialite');
            if (pI) activePillars.push('integrite');
            if (pD) activePillars.push('disponibilite');
            if (pT) activePillars.push('tracabilite');
            if (pP) activePillars.push('preuve');
            if (pA) activePillars.push('Auditabilite'); // Note: Capital A as per constants

            groupedValues[vmName] = {
              name: vmName,
              description: vmDesc,
              nature: vmNature || 'processus',
              proprietaire: {
                nom: vmProprietaireNom || '',
                type: vmProprietaireType || 'interne'
              },
              securityPillars: activePillars,
              supportAssets: []
            };
          }

          if (bsName) {
            groupedValues[vmName].supportAssets.push({
              name: bsName,
              description: bsDesc,
              type: bsType,
              owner: bsOwner,
              location: bsLocation,
              // Enhanced CTI fields
              vendor: bsVendor,
              product: bsProduct,
              version: bsVersion,
              cpe: (bsVendor && bsProduct) ? `cpe:2.3:a:${bsVendor.toLowerCase()}:${bsProduct.toLowerCase()}:${bsVersion ? bsVersion.toLowerCase() : '*'}:*:*:*:*:*:*:*` : '',
              cpeConfidence: (bsVendor && bsProduct) ? 'Generated' : ''
            });
          }
        });

        // Convert grouped values to array format and generate IDs
        const maxVmNumber = businessValues.reduce((max, v) => {
          const match = v.shortId?.match(/^VM(\d+)$/);
          return match ? Math.max(max, parseInt(match[1], 10)) : max;
        }, 0);
        const newValues = Object.values(groupedValues).map((group, index) => {
          // Generate Business Value ID using crypto.randomUUID() for robustness
          // Fallback to simpler ID if crypto not available (unlikely in modern browsers)
          const businessValueId = typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `imported-vm-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

          // Generate Support Asset IDs
          const supportAssets = group.supportAssets.map((asset, assetIndex) => {
            const assetId = typeof crypto !== 'undefined' && crypto.randomUUID
              ? crypto.randomUUID()
              : `imported-bs-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

            // Calculate shortId - we need to fetch the next available ID normally, 
            // but for bulk import we estimate based on current length + index
            const shortId = getShortId(maxVmNumber + index);
            const assetShortId = getSupportAssetShortId(shortId, assetIndex);

            return {
              ...asset,
              id: assetId,
              shortId: assetShortId
            };
          });

          return {
            id: businessValueId,
            name: group.name,
            description: group.description,
            nature: group.nature || 'processus',
            proprietaire: group.proprietaire || { nom: '', type: 'interne' },
            shortId: getShortId(maxVmNumber + index),
            securityPillars: group.securityPillars || [], // Fix: Use the parsed security pillars
            supportAssets: supportAssets
          };
        });

        if (newValues.length > 0) {
          setBusinessValues(prev => {
            const next = [...prev, ...newValues];
            businessValuesRefLatest.current = next;
            return next;
          });
          setHasUnsavedChanges(true);
          markModified();
          showSuccessToast(t('businessValues.status.importSuccess', { count: newValues.length }) || `${newValues.length} valeurs métier importées avec succès`);
        } else {
          showInfoToast('Aucune donnée valide trouvée dans le fichier CSV');
        }

      } catch (error) {
        console.error('Error parsing CSV:', error);
        showErrorToast('Erreur lors de l\'import du fichier CSV');
      }
    };

    reader.readAsText(file);
  };

  // Show loading state
  if (isWorkshopDataLoading) {
    return (
      <div className="bg-gray-50 p-6 rounded-lg flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('businessValues.status.saving')}</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (workshopDataError?.businessValues) {
    return (
      <div className="bg-gray-50 p-6 rounded-lg">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
          <h3 className="font-bold mb-2">Error Loading Data</h3>
          <p>{workshopDataError.businessValues}</p>
          <button
            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={formChangeRef} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <BusinessValuesHeader
        viewMode={viewMode}
        setViewMode={setViewMode}
        handleSaveData={handleSaveData}
        onAddClick={() => setIsAddFormVisible(!isAddFormVisible)}
        handleExportPDF={handleExportPDF}
        handleExportPDFEn={handleExportPDFEn}
        handleExportDocx={handleExportDocx}
        handleExportDocxEn={handleExportDocxEn}
        isAddFormVisible={isAddFormVisible}
        businessValues={businessValues}
        onOpenGuide={() => setIsGuideOpen(true)}
        onImport={handleImport}
      />

      {/* Guide Modal - positioned right under the header */}
      <GuideModal isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          {/* Add Button - Simplified */}
          <button
            onClick={() => setIsAddFormVisible(!isAddFormVisible)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-sm text-sm font-medium"
          >
            <PlusCircle size={18} className="mr-2" />
            {isAddFormVisible ? t('common.cancel') : t('businessValues.buttons.add')}
          </button>
        </div>

        {/* Help Toggle */}
        <button
          onClick={() => setIsHelpVisible(!isHelpVisible)}
          className="flex items-center text-sm text-blue-600 hover:text-blue-800 underline"
        >
          <Info size={16} className="mr-1" />
          {isHelpVisible ? (t('businessValues.guide.title') || "Help & Guide") : (t('businessValues.help.show') || "Show Help")}
        </button>
      </div>

      {/* Inline form for adding business values */}
      <InlineAddForm
        isVisible={isAddFormVisible}
        newBusinessValue={newBusinessValue}
        setNewBusinessValue={setNewBusinessValue}
        newBusinessValueDescription={newBusinessValueDescription}
        setNewBusinessValueDescription={setNewBusinessValueDescription}
        newNature={newNature}
        setNewNature={setNewNature}
        newProprietaireNom={newProprietaireNom}
        setNewProprietaireNom={setNewProprietaireNom}
        newProprietaireType={newProprietaireType}
        setNewProprietaireType={setNewProprietaireType}
        handleAddBusinessValue={handleAddBusinessValue}
        onCancel={handleCancelAddForm}
      />

      {/* Contenu principal avec carte et ombre */}
      <motion.div
        className="mt-6 border rounded-lg shadow-sm overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <AnimatePresence mode="wait">
          {viewMode === 'list' ? (
            <BusinessValueListView
              businessValues={businessValues}
              expandedValue={expandedValue}
              setExpandedValue={setExpandedValue}
              editingValue={editingValue}
              setEditingValue={setEditingValue}
              editValue={editValue}
              setEditValue={setEditValue}
              editDescription={editDescription}
              setEditDescription={setEditDescription}
              editNature={editNature}
              setEditNature={setEditNature}
              editProprietaireNom={editProprietaireNom}
              setEditProprietaireNom={setEditProprietaireNom}
              editProprietaireType={editProprietaireType}
              setEditProprietaireType={setEditProprietaireType}
              handleStartEdit={handleStartEdit}
              handleEditSave={handleEditSave}
              handleCancelEdit={handleCancelEdit}
              handleRemoveBusinessValue={handleRemoveBusinessValue}
              handleToggleSecurityPillar={handleToggleSecurityPillar}
              handleAddSupportAsset={handleAddSupportAsset}
              newSupportAssetName={newSupportAssetName}
              setNewSupportAssetName={setNewSupportAssetName}
              newSupportAssetDescription={newSupportAssetDescription}
              setNewSupportAssetDescription={setNewSupportAssetDescription}
              newSupportAssetType={newSupportAssetType}
              setNewSupportAssetType={setNewSupportAssetType}
              newSupportAssetOwner={newSupportAssetOwner}
              setNewSupportAssetOwner={setNewSupportAssetOwner}
              newSupportAssetLocation={newSupportAssetLocation}
              setNewSupportAssetLocation={setNewSupportAssetLocation}
              // CPE fields
              newSupportAssetVendor={newSupportAssetVendor}
              setNewSupportAssetVendor={setNewSupportAssetVendor}
              newSupportAssetProduct={newSupportAssetProduct}
              setNewSupportAssetProduct={setNewSupportAssetProduct}
              newSupportAssetVersion={newSupportAssetVersion}
              setNewSupportAssetVersion={setNewSupportAssetVersion}
              discoveredCPE={discoveredCPE}
              cpeConfidence={cpeConfidence}
              cpeSearching={cpeSearching}
              assetTypes={ASSET_TYPES}
              allExistingSupportAssets={allExistingSupportAssets}
              handleLinkExistingSupportAsset={handleLinkExistingSupportAsset}
              editingAsset={editingAsset}
              handleStartEditSupportAsset={handleStartEditSupportAsset}
              handleCancelEditSupportAsset={handleCancelEditSupportAsset}
              handleEditSupportAssetChange={handleEditSupportAssetChange}
              handleEditSupportAssetSave={handleEditSupportAssetSave}
              handleRemoveSupportAsset={handleRemoveSupportAsset}
              handleGlobalDeleteSupportAsset={handleGlobalDeleteSupportAsset}
            />
          ) : (
            <BusinessValueChartView
              businessValues={businessValues}
              selectedValueForChart={selectedValueForChart}
              setSelectedValueForChart={setSelectedValueForChart}
              useShortIds={useShortIds}
              setUseShortIds={setUseShortIds}
              handleToggleSecurityPillar={handleToggleSecurityPillar}
              handleRemoveSupportAsset={handleRemoveSupportAsset}
              setExpandedValue={setExpandedValue}
              setViewMode={setViewMode}
              zoomLevel={zoomLevel}
              setZoomLevel={setZoomLevel}
              viewportPosition={viewportPosition}
              setViewportPosition={setViewportPosition}
              isDragging={isDragging}
              setIsDragging={setIsDragging}
              dragStart={dragStart}
              setDragStart={setDragStart}
              chartContainerRef={chartContainerRef}
            />
          )}
        </AnimatePresence>
      </motion.div>

      {/* Bottom Save Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSaveData}
          className="px-6 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-sm text-sm font-medium flex items-center"
        >
          <Save size={16} className="mr-2" />
          {t('businessValues.buttons.save')}
        </button>
      </div>

      {/* Collapsible Section d'aide */}
      <AnimatePresence>
        {isHelpVisible && (
          <motion.div
            className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200"
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: 'auto', marginTop: 24 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setIsHelpVisible(false)}
            >
              <motion.h3
                className="flex items-center text-gray-700 font-medium"
                initial={{ y: -10 }}
                animate={{ y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                {t('businessValues.help.title')}
              </motion.h3>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsHelpVisible(false);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title={t('common.close') || 'Fermer'}
              >
                <X size={16} />
              </button>
            </div>
            <motion.p
              className="text-sm text-gray-600 mt-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {t('businessValues.help.description')}
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default BusinessValues;







